import { CircularProgress, Grid, IconButton } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams,useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import useJobPolling from "hooks/useJobPolling";
import useDefectNavContext from "hooks/useDefectNavContext";
import RestoreIcon from "@mui/icons-material/Restore";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";

import { appPath, defectsPath } from "helpers/routePaths";
import { getLocalStorageData } from "helpers/utils";
import { changeDateForSapError } from "helpers/date";
import {
  DEFECT_NOTIFICATION_REFRESH_EVENT,
  HistoryCaptions,
  NoReadOnly,
} from "helpers/constants";
import ColourConstants from "helpers/colourConstants";
import { READONLY_ACCESS } from "constants/AccessTypes/AccessTypes";
import API from "helpers/api";

import {
  getDefectDetail,
  raiseNotification,
} from "services/defects/details";
import { getIntegrationDefinitions, getIntegrationInstances } from "services/integrations";
import { defectsPage } from "services/History/models";

import {
  setHistoryDrawerState,
  showError,
} from "../../../redux/common/actions";

import ActionButtonStyle from "styles/application/ActionButtonStyle";
import { makeStyles } from "tss-react/mui";

import NavDetails from "components/Elements/NavDetails";
import TabTitle from "components/Elements/TabTitle";
import AccessWrapper from "components/Modules/AccessWrapper";
import HistoryBar from "components/Modules/HistorySidebar/HistoryBar";

import Notes from "./Notes";
import DefectImages from "./Images";
import ChangeStatusPopup from "./ChangeStatusPopup";
import Details from "./details";
import Audio from "./Audio";
import Parts from "./Parts";
import IntegrationName from "./IntegrationName";

const AT = ActionButtonStyle();
const media = "@media (max-width: 414px)";

/**
 * Styles
 */
const useStyles = makeStyles()((theme) => ({
  restore: {
    border: "2px solid",
    borderRadius: "100%",
    height: "35px",
    width: "35px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#307ad6",
  },
  buttons: {
    display: "flex",
    marginLeft: "auto",
    [media]: {
      marginLeft: "0px",
      flexDirection: "column",
      marginBottom: "10px",
      gap: "10px",
    },
  },
  raiseDefectButton: {
    display: "flex",
    marginLeft: "auto",
    backgroundColor: "yellow",
    [media]: {
      marginLeft: "0px",
      flexDirection: "column",
      marginBottom: "10px",
      gap: "10px",
    },
  },
  wrapper: {
    display: "flex",
    [media]: {
      marginTop: "10px",
      justifyContent: "space-between",
      flexDirection: "column",
    },
  },
  navPill: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #d0d0d0",
    borderRadius: "20px",
    padding: "0 2px",
    gap: "2px",
  },
  navPillIconButton: {
    padding: "4px",
  },
  navPillText: {
    fontSize: "14px",
    fontWeight: "bold",
    minWidth: "36px",
    textAlign: "center",
    fontFamily: "Roboto Condensed",
  },
}));

/**
 * SX (styled) objects for inline usage with MUI `sx` prop
 */
const changeStatusButtonSx = {
  "&.MuiButton-root": {
    backgroundColor: "#ED8738",
  },
};

const raiseNotificationButtonSx = {
  textTransform: "uppercase",
  "&.MuiButton-root": {
    backgroundColor: "#23BB79",
    color: "white",
  },
  "&.Mui-disabled": {
    backgroundColor: "#d5d5d5",
    color: "grey",
  },
};

const getNotificationCount = (data) =>
  Array.isArray(data)
    ? data.length
    : typeof data === "number"
    ? data
    : typeof data === "object"
    ? data?.unacknowledgedCount ?? 0
    : 0;

function DefectsDetails() {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { isHistoryDrawerOpen } = useSelector((state) => state.commonData);
  const { id } = useParams();

  const { customCaptions, siteAppID, application, position } =
    getLocalStorageData("me");

  const { navContext, hasPrevious, hasNext, goToPrevious, goToNext } =
    useDefectNavContext();

  const [isChangeStatusOpen, setIsChangeStatusOpen] = useState(false);
  const [integrationInstance, setIntegrationInstance] = useState(null);
  const [integrationDefinitions, setIntegrationDefinitions] = useState(null);
  const [defectDetails, setDefectDetails] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isRaiseDefectLoading, setIsRaiseDefectLoading] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const acknowledgeTimeoutRef = useRef(null);

  const readOnly = position?.defectAccess === READONLY_ACCESS;
  const canAcknowledgeDefects = position?.canAcknowledgeDefects === true;

  // Poll for job status when a job is queued
  const { jobStatus, isPolling, error: pollingError } = useJobPolling(currentJobId);

  const handleToggleChangeStatus = () =>
    setIsChangeStatusOpen((prev) => !prev);

  /**
   * Fetch defect details and integration instance
   */
  const handleFetchDefect = useCallback(async () => {
    try {
      const [defectResponse, integrationResponse, definitionsResponse] = await Promise.all([
        getDefectDetail(id),
        getIntegrationInstances(),
        getIntegrationDefinitions(),
      ]);

      if (defectResponse.status) {
        setDefectDetails(defectResponse.data);
      }

      if (definitionsResponse.status) {
        setIntegrationDefinitions(definitionsResponse.data);
      }

      // Handle integration instances - filter active defect integrations
      if (integrationResponse?.status && definitionsResponse?.status) {
        // Get all defect integration types
        const defectIntegrationTypes = definitionsResponse.data
          ?.filter(def => def.isDefectIntegration)
          .map(def => def.integrationType);

        // Filter for active defect integrations
        const activeDefectInstances = integrationResponse.data?.filter(
          instance => instance.isActive && 
          defectIntegrationTypes.includes(instance.integrationType)
        ) || [];

        // Only set instance if we have active defect integrations
        if (activeDefectInstances.length > 0) {
          const rawInstance = activeDefectInstances[0];
          const formattedInstance = {
            ...rawInstance,
            fieldMappings: rawInstance?.fieldMappings
              ? JSON.parse(rawInstance?.fieldMappings) ?? {}
              : {},
            settings: rawInstance?.settings
              ? JSON.parse(rawInstance?.settings) ?? {}
              : {}
          };
          setIntegrationInstance(formattedInstance);
        } else {
          setIntegrationInstance(null);
        }
      }
    } catch (error) {
      console.error("Error fetching defect details:", error);
      setIntegrationInstance(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    handleFetchDefect();
  }, [handleFetchDefect]);

  useEffect(() => {
    if (!canAcknowledgeDefects) {
      return;
    }

    if (!defectDetails?.isNew) {
      return;
    }

    if (acknowledgeTimeoutRef.current) {
      return;
    }

    let isActive = true;

    const acknowledgeDefect = async () => {
      acknowledgeTimeoutRef.current = setTimeout(async () => {
        try {
          await API.post(`/api/defects/${id}/acknowledge`);
          const response = await API.get("/api/defects/unacknowledged");
          window.dispatchEvent(
            new CustomEvent(DEFECT_NOTIFICATION_REFRESH_EVENT, {
              detail: {
                count: getNotificationCount(response?.data),
              },
            })
          );
        } catch {
          if (isActive) {
            dispatch(showError(t("validation.failedToAcknowledgeDefect")));
          }
        } finally {
          if (isActive) {
            acknowledgeTimeoutRef.current = null;
          }
        }
      }, 1000);
    };

    acknowledgeDefect();

    return () => {
      isActive = false;
      if (acknowledgeTimeoutRef.current) {
        clearTimeout(acknowledgeTimeoutRef.current);
        acknowledgeTimeoutRef.current = null;
      }
    };
  }, [canAcknowledgeDefects, defectDetails?.isNew, dispatch, id, t]);

  // Handle job polling status changes
  useEffect(() => {
    // Update loading state based on polling status
    setIsRaiseDefectLoading(isPolling);

    // Handle job completion
    if (jobStatus && !isPolling && jobStatus.completedAt) {
      // Check if job succeeded or failed based on the message
      const message = jobStatus.message || "";
      const isFailed = message.toLowerCase().includes("sync failed") ||
                      message.toLowerCase().includes("error");

      if (isFailed) {
        // Job failed - show error and re-enable button
        // Remove escaped quotes from the message if present
        let cleanMessage = message;
        try {
          // Try to parse as JSON in case it's double-encoded
          cleanMessage = JSON.parse(message);
        } catch {
          // If not JSON, just remove surrounding quotes
          cleanMessage = message.replace(/^["']|["']$/g, '');
        }
        dispatch(showError(cleanMessage || t("validation.defectSyncFailed")));
        setCurrentJobId(null);
      } else {
        // Job succeeded - just update button state (re-enable)
        setCurrentJobId(null);
        handleFetchDefect();
      }
    }

    // Handle polling error (including timeout)
    if (pollingError && !isPolling) {
      dispatch(showError(pollingError));
      setCurrentJobId(null);
    }
  }, [jobStatus, isPolling, pollingError, dispatch, t]);

  const defectStatus = {
    exported: defectDetails?.exported,
    sapError: defectDetails?.sapError,
    sapExportDateTime: defectDetails?.sapExportDateTime,
  };

  const isSynchronized =
    !defectDetails?.sapError && defectDetails?.sapExportDateTime;
  const hasError = !!defectDetails?.sapError;

  // Memoize the canRaiseNotification check to avoid repeated calculations
  const canShowRaiseNotification = useMemo(() => {
    if (!integrationInstance?.id || !integrationDefinitions) return false;

    // Find the definition matching the instance's type
    const definition = integrationDefinitions.find(
      def => def.integrationType === integrationInstance.integrationType
    );
    
    if (!definition?.isDefectIntegration) return false;

    // Check if defect's risk rating ID matches integration settings
    const defectRiskRatingKeys = integrationInstance?.fieldMappings?.EnumValue?.DefectRiskRating;

    // If DefectRiskRating mapping exists, check if current defect's risk rating is in the mapping
    if (defectRiskRatingKeys && typeof defectRiskRatingKeys === 'object' && Object.keys(defectRiskRatingKeys).length > 0) {
      const defectRiskRatingId = defectDetails?.defectRiskRatingID;
      // Only show button if the defect's risk rating ID is in the mapping
      return defectRiskRatingKeys.hasOwnProperty(String(defectRiskRatingId));
    }

    // If no DefectRiskRating mapping exists or is empty, show button by default
    return true;
  }, [integrationInstance, integrationDefinitions, defectDetails?.defectRiskRatingID]);

  const handleRaiseDefect = async () => {
    setIsRaiseDefectLoading(true);
    try {
      const response = await raiseNotification(id);

      // Check for jobId first (async job response)
      if (response.data?.jobId) {
        // Background job was queued - start polling
        setCurrentJobId(response.data.jobId);
        // Note: isRaiseDefectLoading will be managed by the polling useEffect
      } else if (!response.status) {
        // Handle known error (e.g., duplicate notification)
        // Ensure we only set string error messages, not objects
        const errorMessage = typeof response.data === 'string'
          ? response.data
          : response.data?.message || t("validation.failedToRaiseNotification");

        setDefectDetails((prev) => ({
          ...prev,
          sapError: errorMessage,
          sapExportDateTime: null,
        }));
        dispatch(showError(errorMessage));
        setIsRaiseDefectLoading(false);
      } else {
        // Legacy synchronous success: refetch the latest data from the server
        await handleFetchDefect();
        setIsRaiseDefectLoading(false);
      }
    } catch (error) {
      // Generic server error
      await handleFetchDefect();
      dispatch(showError(error?.data ?? t("validation.failedToRaiseNotification")));
      setIsRaiseDefectLoading(false);
    }
  };

  if (isLoading) return <CircularProgress />;

  return (
    <div className="container">
      <TabTitle
        title={`${customCaptions.defect} ${defectDetails?.number} | ${application.name}`}
      />
      <ChangeStatusPopup
        open={isChangeStatusOpen}
        onClose={handleToggleChangeStatus}
        setDetails={setDefectDetails}
        fetchDefect={handleFetchDefect}
      />

      <HistoryBar
        id={id}
        showhistorybar={isHistoryDrawerOpen}
        dispatch={dispatch}
        fetchdata={(id, pageNumber, pageSize) =>
          defectsPage(id, pageNumber, pageSize)
        }
        origin={HistoryCaptions.defects}
      />

      <div className="topContainerCustomCaptions">
        <NavDetails
          status
          lastSaved={null}
          staticCrumbs={[
            {
              id: 1,
              name: customCaptions?.defectPlural || "Defects",
              url: `${appPath}${defectsPath}`,
            },
            {
              id: 2,
              name: defectDetails.number,
            },
          ]}
          hideLastLogin
          hideVersion
          showCreatedByAt
          showAcknowledgedByAt={false}
          showSDefects
          state={{
            modelStatusName: defectDetails?.defectStatusName,
            statusColor:
              defectDetails.defectStatusType === "C"
                ? ColourConstants.green
                : ColourConstants.red,
          }}
          defectDetails={{ defectDetail: defectStatus, defectIntegrationInstance: integrationInstance }}
          time={defectDetails.createdDateTime}
          userName={defectDetails.createdUserName}
          acknowledgedTime={null}
          acknowledgedUserName={null}
          afterCrumbs={
            navContext && (
              <div className={classes.navPill}>
                <IconButton
                  aria-label={`Previous ${customCaptions?.defect || "Defect"}`}
                  onClick={goToPrevious}
                  disabled={!hasPrevious}
                  size="small"
                  className={classes.navPillIconButton}
                >
                  <KeyboardArrowLeft fontSize="small" />
                </IconButton>
                <span className={classes.navPillText} aria-live="polite">
                  {navContext.pos + 1}/{navContext.idList.length}
                </span>
                <IconButton
                  aria-label={`Next ${customCaptions?.defect || "Defect"}`}
                  onClick={goToNext}
                  disabled={!hasNext}
                  size="small"
                  className={classes.navPillIconButton}
                >
                  <KeyboardArrowRight fontSize="small" />
                </IconButton>
              </div>
            )
          }
        />

        <div className={classes.wrapper}>
          {/* Raise Notification Button */}
          <AccessWrapper access={position?.defectAccess} accessList={NoReadOnly}>
            <div className={classes.buttons}>
              {canShowRaiseNotification && (
                <LoadingButton
                  sx={{
                    ...raiseNotificationButtonSx,
                    height: "37px !important",
                    minHeight: "37px",
                    maxHeight: "37px",
                    fontSize: "13.5px",
                    fontWeight: "bold",
                    fontFamily: "Roboto Condensed",
                    marginRight: "10px",
                    padding: "6px 16px",
                    whiteSpace: "nowrap",
                    "& .MuiLoadingButton-loadingIndicator": {
                      position: "relative",
                      left: 0,
                      marginRight: "8px",
                    },
                  }}
                  onClick={handleRaiseDefect}
                  className={classes.raiseDefectButton}
                  disabled={isSynchronized}
                  loading={isRaiseDefectLoading}
                  loadingPosition="start"
                  loadingIndicator={
                    <CircularProgress size={16} sx={{ color: "#666" }} />
                  }
                  variant="contained"
                >
                  {t("action.raiseNotification")}
                </LoadingButton>
              )}
            </div>
          </AccessWrapper>

          {/* Change Status Button */}
          <AccessWrapper access={position?.defectAccess} accessList={NoReadOnly}>
            <div className={classes.buttons}>
              <AT.GeneralButton
                sx={changeStatusButtonSx}
                onClick={handleToggleChangeStatus}
              >
                {t("action.changeStatus")}
              </AT.GeneralButton>
            </div>
          </AccessWrapper>

          {/* History Drawer Icon */}
          <div
            className="restore"
            onClick={() => dispatch(setHistoryDrawerState(true))}
          >
            <RestoreIcon className={classes.restore} />
          </div>
        </div>
      </div>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Details
            details={defectDetails}
            siteAppID={siteAppID}
            captions={customCaptions}
            defectId={id}
            fetchDefect={handleFetchDefect}
            isReadOnly={readOnly}
            defectDetail={defectStatus}
          />
        </Grid>

        <Grid item xs={12} lg={6}>
          <Grid item xs={12}>
            <DefectImages
              defectId={id}
              captions={customCaptions}
              isReadOnly={readOnly}
            />
          </Grid>

          <Grid item xs={12} style={{ marginTop: "16px" }}>
            <Notes defectId={id} isReadOnly={readOnly} />
          </Grid>
        </Grid>

        <Grid item xs={12} lg={6}>
          {defectDetails?.audioURL && (
            <Grid item xs={12} style={{ marginBottom: "16px" }}>
              <Audio
                src={defectDetails?.audioURL}
                defectId={id}
                isReadOnly={readOnly}
                setDetails={setDefectDetails}
              />
            </Grid>
          )}

          {application?.showDefectParts && (
            <Grid item xs={12}>
              <Parts
                captions={customCaptions}
                defectId={id}
                isReadOnly={readOnly}
              />
            </Grid>
          )}

          {hasError && (
            <Grid item xs={12} id="integration-name-status">
              <IntegrationName
                integrationName={`${integrationInstance?.name}`}
                lastAttempt={changeDateForSapError(
                  integrationInstance?.lastSyncAttempt
                )}
                sapError={defectStatus?.sapError}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </div>
  );
}

export default DefectsDetails;
